import { getSupabaseClient } from "../../providers/supabase/supabase";
import type {
  CompanyMatchedLead,
  EnrichedLead,
  LeadboxerEventsResponse,
  LeadboxerInstance,
  LeadboxerSessionsResponse,
  LeadboxerEvent,
  SessionWithEvents,
} from "./types";
import { extractLeadId } from "./utils";

async function readFunctionErrorMessage(error: unknown): Promise<string> {
  const fallback =
    error instanceof Error ? error.message : "Failed to call Leadboxer";
  const context = (error as { context?: Response })?.context;
  if (context && typeof context.json === "function") {
    try {
      const payload = await context.json();
      if (payload && typeof payload === "object" && "message" in payload) {
        return String(payload.message);
      }
    } catch {
      // ignore parse errors
    }
  }
  return fallback;
}

async function invokeLeadboxer<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await getSupabaseClient().functions.invoke(
    "leadboxer",
    { body },
  );

  if (error) {
    throw new Error(await readFunctionErrorMessage(error));
  }

  if (
    data &&
    typeof data === "object" &&
    "message" in data &&
    "status" in data &&
    typeof data.status === "number" &&
    data.status >= 400
  ) {
    throw new Error(String(data.message));
  }

  return data as T;
}

export async function fetchLeadboxerLeads(
  instance: LeadboxerInstance,
  period: number,
): Promise<EnrichedLead[]> {
  const data = await invokeLeadboxer<{ leads: EnrichedLead[] }>({
    action: "leads",
    instance,
    period,
  });
  return data.leads ?? [];
}

export async function fetchLeadboxerCompanyLeads(
  companyId: number,
  period: number,
): Promise<CompanyMatchedLead[]> {
  const data = await invokeLeadboxer<{ leads: CompanyMatchedLead[] }>({
    action: "companyLeads",
    companyId,
    period,
  });
  return data.leads ?? [];
}

export async function fetchLeadboxerEvents(
  instance: LeadboxerInstance,
  sessionId: string,
): Promise<LeadboxerEvent[]> {
  const data = await invokeLeadboxer<LeadboxerEventsResponse>({
    action: "events",
    instance,
    sessionId,
  });

  if (data.responseCode !== 0) {
    throw new Error(data.responseMessage || "Failed to fetch events");
  }

  return data.events ?? [];
}

export async function fetchLeadboxerSessions(
  instance: LeadboxerInstance,
  leadCardLink: string,
): Promise<SessionWithEvents[]> {
  const leadId = extractLeadId(leadCardLink);
  if (!leadId) {
    throw new Error("Could not extract leadId from lead card link");
  }

  const sessionsData = await invokeLeadboxer<LeadboxerSessionsResponse>({
    action: "sessions",
    instance,
    leadId,
  });

  if (sessionsData.responseCode !== 0) {
    throw new Error(sessionsData.responseMessage || "Failed to fetch sessions");
  }

  const sessionsWithEvents = await Promise.all(
    (sessionsData.sessions ?? []).map(async (session) => {
      try {
        const events = await fetchLeadboxerEvents(instance, session.session_id);
        return { session, events };
      } catch {
        return { session, events: [] as LeadboxerEvent[] };
      }
    }),
  );

  return sessionsWithEvents;
}
